import { useState } from "react";
import { Navigate } from "react-router";
import { Body, H1 } from "@datapay-by-datacom/design-system";
import { CompanyContextStep } from "../../onboarding";
import type { CompanyContext } from "../../onboarding";
import { MatchingWizardProvider } from "../components/MatchingWizardProvider";
import { PayRunIdStep } from "../components/PayRunIdStep";
import { useComponentMatchingImportEnabled } from "../hooks/useComponentMatchingImportEnabled";
import { MatchingImportComposer } from "../screens/MatchingImportComposer";
import { isPayRunWritable, type BoundPayRun } from "../types/matching";
import type { MyPayEnvironment } from "../../generated/core-api";
import { COMPONENT_MATCHING_IMPORT_DISPLAY_NAME } from "../config";
import s from "./ComponentMatchingImportLayout.module.css";

/**
 * Bind pay run ID first (Option A): company → pay run → upload matching CSV.
 * PayRunId is not a CSV column — every row is stamped from the bound run.
 */
export function ComponentMatchingImportLayout() {
  const { isLoaded, areFlagsOn } = useComponentMatchingImportEnabled();
  const [boundContext, setBoundContext] = useState<CompanyContext | null>(null);
  const [boundPayRun, setBoundPayRun] = useState<BoundPayRun | null>(null);
  /** True while the composer has uncommitted progress or is validating/committing. */
  const [importLocked, setImportLocked] = useState(false);

  if (!isLoaded) {
    return null;
  }

  if (!areFlagsOn) {
    return <Navigate to="/" replace />;
  }

  const showComposer =
    boundContext !== null &&
    boundPayRun !== null &&
    isPayRunWritable(boundPayRun.state);

  return (
    <div className={s.container}>
      <header className={s.pageHeader}>
        <H1>{COMPONENT_MATCHING_IMPORT_DISPLAY_NAME}</H1>
        <Body className={s.pageSubtitle}>
          Upload a parallel-run variance CSV and apply or delete amount matches
          on Main pay packets.
        </Body>
      </header>

      <CompanyContextStep
        disabled={importLocked}
        onContextBound={(ctx) => {
          setBoundContext(ctx);
          setBoundPayRun(null);
        }}
        onContextCleared={() => {
          setBoundContext(null);
          setBoundPayRun(null);
        }}
      />

      {boundContext && (
        <PayRunIdStep
          companyCode={boundContext.companyCode}
          mypayEnvironment={boundContext.mypayEnvironment as MyPayEnvironment}
          boundPayRun={boundPayRun}
          onBound={setBoundPayRun}
          onCleared={() => setBoundPayRun(null)}
          disabled={importLocked}
        />
      )}

      {showComposer && boundContext && boundPayRun && (
        <MatchingWizardProvider
          key={`${boundContext.companyCode}:${boundPayRun.payRunId}`}
          companyCode={boundContext.companyCode}
          companyName={boundContext.companyName}
          mypayEnvironment={boundContext.mypayEnvironment as MyPayEnvironment}
          boundPayRun={boundPayRun}
        >
          <MatchingImportComposer onImportLockChange={setImportLocked} />
        </MatchingWizardProvider>
      )}
    </div>
  );
}
